import * as React from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '../lib/cn';
import type { Toast, ToastVariant } from '../hooks/use-toast';

// ── Icon + style maps ────────────────────────────────────────────────────────

const ICON_MAP: Record<ToastVariant, React.ElementType> = {
  default: Info,
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
};

const ICON_CLASS: Record<ToastVariant, string> = {
  default: 'text-primary',
  success: 'text-success',
  error: 'text-destructive',
  warning: 'text-warning',
};

const BORDER_CLASS: Record<ToastVariant, string> = {
  default: 'border-border',
  success: 'border-success/40',
  error: 'border-destructive/40',
  warning: 'border-warning/40',
};

const ACCENT_CLASS: Record<ToastVariant, string> = {
  default: 'bg-primary/5',
  success: 'bg-success/5',
  error: 'bg-destructive/5',
  warning: 'bg-warning/5',
};

// ── Component ────────────────────────────────────────────────────────────────

export interface ToastItemProps {
  item: Toast;
  onDismiss: (id: string) => void;
}

export function ToastItem({ item, onDismiss }: ToastItemProps) {
  const variant = item.variant ?? 'default';
  const Icon = ICON_MAP[variant];

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      className={cn(
        'relative flex items-start gap-3 w-full rounded-xl border bg-card px-4 py-3.5 shadow-lg',
        'animate-toast-in',
        BORDER_CLASS[variant],
      )}
    >
      {/* Coloured left accent bar */}
      <span
        className={cn('absolute left-0 top-3 bottom-3 w-1 rounded-full', ACCENT_CLASS[variant])}
        aria-hidden
      />

      <Icon
        className={cn('h-5 w-5 shrink-0 mt-0.5 ml-2', ICON_CLASS[variant])}
        aria-hidden
      />

      <div className="flex-1 min-w-0">
        {item.title && (
          <p className="text-sm font-semibold text-card-foreground leading-snug">
            {item.title}
          </p>
        )}
        {item.description && (
          <p
            className={cn(
              'text-sm text-muted-foreground leading-snug',
              item.title && 'mt-0.5',
            )}
          >
            {item.description}
          </p>
        )}
      </div>

      <button
        onClick={() => onDismiss(item.id)}
        aria-label="Dismiss notification"
        className={cn(
          'shrink-0 rounded-sm p-0.5',
          'text-muted-foreground hover:text-foreground transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
